import React, { useState } from 'react';
import { CheckCircle2Icon, XCircleIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
}
const QUIZ_QUESTIONS: QuizQuestion[] = [{
  question: 'What is the primary hazard when the beam is being lifted to the second floor?',
  options: ['Slips and trips', 'Suspended load hazards', 'Noise exposure', 'Chemical exposure'],
  correctIndex: 1
}, {
  question: 'What should happen if wind increases during the lift?',
  options: ['Continue the lift quickly', 'Add more workers to stabilize', 'Stop the lift immediately', 'Switch to a different crane'],
  correctIndex: 2
}, {
  question: 'Who must be designated before the crane lift begins?',
  options: ['The laborer', 'The ironworker', 'A trained signal person', 'The crane operator decides on the spot'],
  correctIndex: 2
}];
export function Screen19_ForemanQuiz({
  onNext
}: ScreenProps) {
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const question = QUIZ_QUESTIONS[currentQ];
  const isCorrect = selectedOption === question?.correctIndex;
  const handleSelect = (index: number) => {
    if (answered) return;
    setSelectedOption(index);
  };
  const handleConfirm = () => {
    if (selectedOption === null) return;
    if (!answered) {
      setAnswered(true);
      if (selectedOption === question.correctIndex) {
        setScore(s => s + 1);
      }
    } else {
      // Move to next question
      if (currentQ < QUIZ_QUESTIONS.length - 1) {
        setCurrentQ(q => q + 1);
        setSelectedOption(null);
        setAnswered(false);
      } else {
        setFinished(true);
      }
    }
  };
  if (finished) {
    return <div className="flex flex-col h-full" data-id="element-842">
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4" data-id="element-843">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} data-id="element-844">
            <CheckCircle2Icon className="w-16 h-16 text-green-500 mb-4" data-id="element-845" />
          </motion.div>
          <h2 className="text-2xl font-bold text-white mb-2" data-id="element-846">Quiz Complete</h2>
          <div className="text-5xl font-black text-white mb-2 tracking-tighter" data-id="element-847">
            {score}
            <span className="text-2xl text-slate-500" data-id="element-848">
              {' '}
              / {QUIZ_QUESTIONS.length}
            </span>
          </div>
          <p className="text-slate-400 text-sm mb-6" data-id="element-849">
            {score === QUIZ_QUESTIONS.length ? 'Perfect score. You have a strong understanding of the PTP.' : 'Review the missed items before starting work.'}
          </p>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 w-full max-w-sm" data-id="element-850">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2" data-id="element-851">
              Waiting for crew
            </p>
            <div className="space-y-2" data-id="element-852">
              {[{
              name: 'Luis',
              role: 'Rigger',
              status: 'completed'
            }, {
              name: 'Dan',
              role: 'Crane Operator',
              status: 'completed'
            }, {
              name: 'Jake',
              role: 'Ironworker',
              status: 'in progress'
            }].map(w => <div key={w.name} className="flex items-center justify-between text-sm" data-id="element-853">
                  <span className="text-white" data-id="element-854">
                    {w.name}{' '}
                    <span className="text-slate-500 text-xs" data-id="element-855">· {w.role}</span>
                  </span>
                  {w.status === 'completed' ? <span className="text-green-400 text-xs font-medium" data-id="element-856">
                      Completed
                    </span> : <span className="text-amber-400 text-xs font-medium" data-id="element-857">
                      In progress...
                    </span>}
                </div>)}
            </div>
          </div>
        </div>

        <div className="pt-4 bg-slate-950" data-id="element-858">
          <PrimaryButton onClick={onNext} data-id="element-859">View Crew Results</PrimaryButton>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-860">
      <div className="mb-5" data-id="element-861">
        <div className="flex items-center justify-between mb-2" data-id="element-862">
          <h2 className="text-2xl font-bold text-white" data-id="element-863">PTP Safety Quiz</h2>
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-900 px-2.5 py-1 rounded-full" data-id="element-864">
            {currentQ + 1} / {QUIZ_QUESTIONS.length}
          </span>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-slate-800 rounded-full h-1.5 flex gap-1" data-id="element-865">
          {QUIZ_QUESTIONS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i < currentQ ? 'bg-green-500' : i === currentQ ? 'bg-orange-500' : 'bg-slate-700'}`} data-id="element-866" />)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-867">
        {/* Question */}
        <AnimatePresence mode="wait" data-id="element-868">
          <motion.div key={currentQ} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.25
        }} data-id="element-869">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-4" data-id="element-870">
              <p className="text-white font-semibold text-base leading-relaxed" data-id="element-871">
                {question.question}
              </p>
            </div>

            {/* Options */}
            <div className="space-y-2.5" data-id="element-872">
              {question.options.map((option, i) => {
              let borderClass = 'border-slate-800';
              let bgClass = 'bg-slate-900';
              let textClass = 'text-slate-300';
              let iconEl = null;
              if (answered) {
                if (i === question.correctIndex) {
                  borderClass = 'border-green-500/50';
                  bgClass = 'bg-green-500/10';
                  textClass = 'text-green-300';
                  iconEl = <CheckCircle2Icon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-873" />;
                } else if (i === selectedOption && i !== question.correctIndex) {
                  borderClass = 'border-red-500/50';
                  bgClass = 'bg-red-500/10';
                  textClass = 'text-red-300';
                  iconEl = <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-874" />;
                }
              } else if (i === selectedOption) {
                borderClass = 'border-orange-500/50';
                bgClass = 'bg-orange-500/10';
                textClass = 'text-orange-200';
              }
              const letter = String.fromCharCode(65 + i);
              return <button key={i} onClick={() => handleSelect(i)} disabled={answered} className={`w-full ${bgClass} border ${borderClass} rounded-xl px-4 py-3.5 flex items-center gap-3 text-left transition-colors`} data-id="element-875">
                    <span className={`w-7 h-7 rounded-full border ${i === selectedOption && !answered ? 'border-orange-500 bg-orange-500/20 text-orange-400' : answered && i === question.correctIndex ? 'border-green-500 bg-green-500/20 text-green-400' : answered && i === selectedOption ? 'border-red-500 bg-red-500/20 text-red-400' : 'border-slate-700 text-slate-500'} flex items-center justify-center text-xs font-bold flex-shrink-0`} data-id="element-876">
                      {letter}
                    </span>
                    <span className={`${textClass} text-sm font-medium flex-1`} data-id="element-877">
                      {option}
                    </span>
                    {iconEl}
                  </button>;
            })}
            </div>

            {/* Feedback after answering */}
            <AnimatePresence data-id="element-878">
              {answered && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25
            }} className={`mt-4 rounded-xl p-4 ${isCorrect ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`} data-id="element-879">
                  <p className={`text-sm font-semibold ${isCorrect ? 'text-green-400' : 'text-red-400'}`} data-id="element-880">
                    {isCorrect ? 'Correct!' : 'Incorrect'}
                  </p>
                  {!isCorrect && <p className="text-slate-300 text-xs mt-1" data-id="element-881">
                      The correct answer is:{' '}
                      <span className="text-white font-medium" data-id="element-882">
                        {question.options[question.correctIndex]}
                      </span>
                    </p>}
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="pt-4 bg-slate-950" data-id="element-883">
        <PrimaryButton onClick={handleConfirm} disabled={selectedOption === null} icon={answered ? <ChevronRightIcon className="w-5 h-5" data-id="element-885" /> : undefined} data-id="element-884">
          {!answered ? 'Confirm Answer' : currentQ < QUIZ_QUESTIONS.length - 1 ? 'Next Question' : 'Finish Quiz'}
        </PrimaryButton>
      </div>
    </div>;
}